import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';

import { Option } from '@amfam/shared/models';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { ValidationService } from '@amfam/ui-kit';

import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';
import { ChoosePaymentAmountModal } from '../../+state/one-time-pay-feature.models';

interface SelectedPaymentAmount {
  billAccountNumber: string;
  amount: string;
}

interface LinkEvent {
  text: string;
  billAccountNumber: string;
}

@Component({
  selector: 'amfam-choose-payment-amount-modal',
  templateUrl: './choose-payment-amount-modal.component.html',
  styleUrls: ['./choose-payment-amount-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChoosePaymentAmountModalComponent implements OnChanges {
  @Input() choosePaymentAmountOptions: ChoosePaymentAmountModal;
  @Input() defaultPaymentAmount?: number;
  @Input() isPastDue: boolean;
  @Output() chooseAmountValues = new EventEmitter<SelectedPaymentAmount>();
  @Output() linkTextEvent = new EventEmitter<LinkEvent>();

  choosePaymentAmountForm: UntypedFormGroup;
  selectedAccountNumber: string;
  linkText: string;
  headingText: string;
  messageText: string;

  postLinkText: string;
  errorBoxText: boolean;
  otherPaymentAmount: UntypedFormControl;
  hasValidationError: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private oneTimePayFacade: OneTimePayFeatureFacade
  ) {}

  ngOnChanges(): void {
    this.getForm();
    this.getAlertMessage();
  }

  getForm(): void {
    this.choosePaymentAmountForm = this.formBuilder.group({
      choosePaymentAmount: [
        this.choosePaymentAmountOptions
          ? this.getDefaultPaymentAmount(
              this.choosePaymentAmountOptions?.radioButtonOptions,
              this.defaultPaymentAmount
            )
          : '',
        Validators.compose([Validators.required])
      ],
      differentPaymentAmount: [
        this.getDefaultPaymentAmount(
          this.choosePaymentAmountOptions?.radioButtonOptions,
          this.defaultPaymentAmount,
          false
        )
      ]
    });
    this.otherPaymentAmount = <UntypedFormControl>(
      this.choosePaymentAmountForm.controls['differentPaymentAmount']
    );
  }

  setOtherAmountValidator() {
    const validatorsList = [
      ValidationService.paymentAmountValidation(true),
      ValidationService.paymentAmountMaxValidation({
        balance: this.getAccountBalance(this.choosePaymentAmountOptions),
        accountType: this.getAccountType(this.choosePaymentAmountOptions)
      })
    ];
    if (this.isPastDue) {
      validatorsList.push(
        ValidationService.minimumDuePaymentPastDueValidator(
          'differentPaymentAmount',
          this.choosePaymentAmountOptions?.radioButtonOptions[0].text,
          this.choosePaymentAmountOptions?.accountDescriptionDetails?.isCancellationLetterSent
        )
      );
    }
    this.choosePaymentAmountForm
      .get('differentPaymentAmount')
      .setValidators(Validators.compose([...validatorsList]));
  }

  getAccountBalance(choosePaymentAmountOptions: ChoosePaymentAmountModal): number {
    const accountBalanceOption = choosePaymentAmountOptions?.radioButtonOptions?.find(
      option => option.id === 'accountBalance'
    );
    return parseFloat(accountBalanceOption?.text?.replace(/\$|,/g, ''));
  }

  getAccountType(choosePaymentAmountOptions: ChoosePaymentAmountModal): string {
    return choosePaymentAmountOptions?.accountDescriptionDetails?.accountType;
  }

  linkEvent(text: string) {
    const linkEventObj: LinkEvent = {
      text: text,
      billAccountNumber:
        this.choosePaymentAmountOptions?.accountDescriptionDetails.billAccountNumber
    };
    this.linkTextEvent.emit(linkEventObj);
  }

  getDefaultPaymentAmount(
    radioOptionList: Option[] = [],
    defaultPaymentAmount: number | string,
    isRadioOption = true
  ) {
    for (const option of radioOptionList) {
      const amount = option?.text?.split('$')[1] || '';
      if (
        ((typeof defaultPaymentAmount == 'number' && amount === defaultPaymentAmount?.toFixed(2)) ||
          amount === defaultPaymentAmount) &&
        isRadioOption
      ) {
        return option;
      } else if (
        (typeof defaultPaymentAmount == 'number' && amount === defaultPaymentAmount?.toFixed(2)) ||
        amount === defaultPaymentAmount
      ) {
        return '';
      }
    }
    if (isRadioOption) {
      return defaultPaymentAmount ? null : radioOptionList[0];
    } else {
      return defaultPaymentAmount;
    }
  }
  getSelectedPaymentAmount(): SelectedPaymentAmount {
    let selectedPaymentAmount: SelectedPaymentAmount;
    if (this.choosePaymentAmountForm.get('choosePaymentAmount').value) {
      selectedPaymentAmount = {
        billAccountNumber:
          this.choosePaymentAmountOptions?.accountDescriptionDetails.billAccountNumber,
        amount: this.choosePaymentAmountForm.get('choosePaymentAmount').value.text
      };
    } else {
      selectedPaymentAmount = {
        billAccountNumber:
          this.choosePaymentAmountOptions?.accountDescriptionDetails.billAccountNumber,
        amount: this.choosePaymentAmountForm.get('differentPaymentAmount').value
      };
    }
    return selectedPaymentAmount;
  }

  getAlertMessage() {
    // this will reset warningbox/errorbox in case of alert message change
    this.headingText = undefined;
    const selectedPaymentAmount = this.getSelectedPaymentAmount();
    this.oneTimePayFacade
      .getAlertMessage(
        this.choosePaymentAmountOptions?.accountDescriptionDetails.billAccountNumber,
        typeof selectedPaymentAmount.amount === 'number'
          ? selectedPaymentAmount.amount
          : Number(selectedPaymentAmount.amount?.replace(/\$|,/g, ''))
      )
      .subscribe(messageObj => {
        if (messageObj.alertMessage) {
          this.headingText = messageObj.alertMessage.heading;
          this.messageText = messageObj.alertMessage.body1;
          this.linkText = messageObj.alertMessage.body2Text;
          this.postLinkText = messageObj.alertMessage.body3;
          this.errorBoxText = messageObj.alertMessage.error;
        }
        if (messageObj?.alertMessage?.title === 'hasLessThanMinDue') {
          this.oneTimePayFacade.sendAnalytics({
            buttonAnalyticName: 'ChoosePaymentAmount:BelowMinimum'
          });
        }
      });
  }
  savePaymentAmount() {
    const selectedPaymentAmount = this.getSelectedPaymentAmount();
    this.choosePaymentAmountForm.controls['differentPaymentAmount'].reset();
    this.choosePaymentAmountForm.updateValueAndValidity();
    this.chooseAmountValues.emit(selectedPaymentAmount);
  }

  radioGroupChangeEvent() {
    this.choosePaymentAmountForm.get('differentPaymentAmount').clearValidators();
    this.choosePaymentAmountForm.get('differentPaymentAmount').updateValueAndValidity();
    this.choosePaymentAmountForm.get('choosePaymentAmount').setValidators(Validators.required);
    this.getAlertMessage();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  textBoxChangeEvent(event: any) {
    this.setOtherAmountValidator();
    this.choosePaymentAmountForm.controls['choosePaymentAmount'].setValue(null);
    if (event.target.value) {
      event.target.value = event.target.value.replace(/\$|,/g, '');
    }
    if (event.target.value !== '') {
      // Do not execute if user has just clicked on text box field and there is no value entered
      this.choosePaymentAmountForm.controls['differentPaymentAmount'].setValue(
        new CurrencyUSDPipe().transform(event.target.value)
      );
      this.getAlertMessage();
      this.clearAmtValidationErrors();
    }
    this.choosePaymentAmountForm.controls['differentPaymentAmount'].markAllAsTouched();
    this.choosePaymentAmountForm.get('differentPaymentAmount').markAsDirty();
    this.hasValidationError =
      this.otherPaymentAmount.hasError('payingLessThanMinDuePastDue') ||
      this.otherPaymentAmount.hasError('paymentGreaterThanZero') ||
      this.otherPaymentAmount.hasError('invalidPaymentAmnt') ||
      this.otherPaymentAmount.hasError('noMoreThanTenOverBalance') ||
      this.otherPaymentAmount.hasError('noMoreThanPersonalLimit') ||
      this.otherPaymentAmount.hasError('noMoreThanCommercialLimit') ||
      this.otherPaymentAmount.hasError('payingLessThanMinDuePastDueCancellationLetterSent');
  }

  private clearAmtValidationErrors() {
    this.choosePaymentAmountForm.get('choosePaymentAmount').markAsPristine();
    this.choosePaymentAmountForm.get('choosePaymentAmount').clearValidators();
    this.choosePaymentAmountForm.get('choosePaymentAmount').updateValueAndValidity();
  }
}
